import {
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  Signal,
  ViewChild,
  effect
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import {
  AiChatApiError,
  AiChatAttachmentModel,
  AiChatFeedbackRating,
  AiChatMessageModel,
  AiChatPendingAttachmentModel
} from '@coreModels';
import { AiChatService } from '../../services/api-services/ai-chat.service';
import { ToastService } from '../../services/toast.service';
import { AiChatConversationStore } from '../../services/ai-chat-conversation.store';
import {
  AiChatClipboardPayload,
  buildAiChatClipboardPayload
} from '../../utils/ai-chat-clipboard';
import {
  AiChatFeedbackDetail,
  AiChatFeedbackModalComponent
} from './ai-chat-feedback-modal/ai-chat-feedback-modal.component';
import { AiChatImagePreviewModalComponent } from './ai-chat-image-preview-modal/ai-chat-image-preview-modal.component';

/** Sentinel for "this surface has not seen a conversation id yet" - see lastSeenConversationId. */
const UNSET_CONVERSATION_ID = Symbol('unset-conversation-id');

/**
 * The shell around the Pebbl conversation, shared by the floating widget (AiChatComponent) and
 * the full page (AskPebblComponent). Templates must expose `#messageList` and `#composerInput`.
 *
 * Everything that outlives a component - the transcript, the turn in flight, the rate-limit
 * lockout - lives in AiChatConversationStore, a root singleton. This class holds only what is
 * genuinely per-surface: the draft being typed, the scroll container and the composer to focus.
 * That split is what lets a reply keep streaming while the customer expands the widget into the
 * full page or navigates away; see the store's own header for what used to happen instead.
 */
@Directive()
export abstract class AiChatConversationBase implements OnDestroy {
  /* ################### Variable Declaration Start ################### */
  /**
   * Bound to the composer textarea. Per-surface on purpose: a half-typed question in the widget
   * is not something to teleport into the full page's composer.
   */
  draftMessage = '';
  /** The API caps a message at 2000 characters. */
  readonly maxMessageLength = AiChatService.MAX_MESSAGE_LENGTH;

  /**
   * The opening line, rendered as the conversation's first assistant turn on both surfaces.
   *
   * Client-side copy, not a real turn: it is never sent to the service, never persisted and
   * never counted in `messages()`. It replaced the old welcome hero, and is rendered as a card
   * with `starterPrompts` beneath it. Held here so the widget and the full page greet identically.
   */
  readonly greeting = "Hi, you're speaking with Pebbl, GatherCo's AI powered assistant. How can I help you today?";

  /**
   * Worked examples shown under the greeting until the customer's first turn.
   *
   * THIS REINSTATES SOMETHING THAT WAS DELIBERATELY REMOVED - read before deleting it again. The
   * welcome hero this replaced had three prompt chips, and they were dropped on the argument that
   * an open question invites the customer's own question where a fixed set mostly advertises what
   * the assistant is NOT being asked. That argument holds for chips beside a hero; it holds less
   * for a panel that opens on a greeting and an empty composer, where the customer has to invent
   * an opening against an assistant whose scope they cannot see. These are framed as examples
   * ("Try asking"), not as the menu, and they are gone the moment there is a real conversation.
   *
   * Client-side copy, exactly like the greeting: never sent as anything other than an ordinary
   * message, never stored, never counted in `messages()`. Nothing downstream knows a turn
   * started here.
   *
   * KEEP THEM ANSWERABLE. Each one has to be a question the service can actually field from the
   * catalogue and the customer's own quotes/orders - a starter that produces "I can't help with
   * that" is worse than no starter at all, because it is the first thing the assistant ever says.
   */
  readonly starterPrompts: readonly string[] = [
    'What stone suits a poolside area?',
    'Which pavers work for a high-traffic driveway?',
    'Show me the status of my recent orders'
  ];

  /* Image attachment limits, mirroring the service's own (ATTACHMENT_MAX_COUNT /
     ATTACHMENT_MAX_BYTES / its content-type allowlist). Enforced here as well so a file that
     cannot possibly be accepted is refused before it is uploaded. SVG is excluded on the service
     side because it renders as markup and is an XSS vector - do not add it. */
  static readonly MAX_ATTACHMENTS = 5;
  static readonly MAX_ATTACHMENT_MB = 10;
  static readonly MAX_ATTACHMENT_BYTES = AiChatConversationBase.MAX_ATTACHMENT_MB * 1024 * 1024;
  static readonly ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
  /** For the file input's `accept`. */
  readonly attachmentAccept = AiChatConversationBase.ACCEPTED_TYPES.join(',');
  readonly maxAttachments = AiChatConversationBase.MAX_ATTACHMENTS;

  /** Images picked for the next turn, with their upload state. Per-surface, like the draft. */
  pendingAttachments: AiChatPendingAttachmentModel[] = [];
  /** Why the last pick was wholly or partly refused - shown under the strip. */
  attachError: string | null = null;
  /** True while a file is dragged over the composer. */
  isDragOver = false;
  /** Distinguishes rows before the service has minted uuids for them. */
  private attachmentSeq = 0;

  protected readonly destroy$ = new Subject<void>();

  /**
   * The conversation this surface last saw, so the effect above can tell a real change from the
   * effect's own first run. A dedicated sentinel rather than `undefined`, because `null` is a
   * legitimate value of `activeConversationId` (signed out, or before the first thread exists).
   */
  private lastSeenConversationId: string | null | typeof UNSET_CONVERSATION_ID = UNSET_CONVERSATION_ID;

  @ViewChild('fileInput') protected fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('messageList') protected messageList?: ElementRef<HTMLDivElement>;
  @ViewChild('composerInput') protected composerInput?: ElementRef<HTMLTextAreaElement>;
  /* ################### Variable Declaration End ################### */

  protected constructor(
    protected store: AiChatConversationStore,
    protected aiChatService: AiChatService,
    protected toastService: ToastService,
    protected modalService: NgbModal,
    protected platformId: Object
  ) {
    // Follow the transcript as it grows. The store touches `messages` once per frame, so this
    // covers a reply streaming in from the *other* surface just as well as from this one -
    // opening the full page mid-turn lands at the bottom and stays there.
    effect(() => {
      this.store.messages();
      this.scrollToLatest();
    });

    // PICKS BELONG TO THE CONVERSATION THEY WERE UPLOADED AGAINST. An image is confirmed with the
    // service against the active `conversation_uuid`, so carrying one into a different
    // conversation would send an attachment id that belongs somewhere else.
    //
    // This used to hang off the widget's startFreshConversation(), which was the only thing that
    // changed conversations from a surface holding picks. That path is gone - opening the panel
    // continues the thread now (see AiChatComponent.toggleChat) - and the two things that DO
    // change it, "New chat" and opening a row from the history rail, are both fired from the rail
    // rather than from a composer. Watching the id itself rather than the callers is what makes
    // this hold for every path, including any added later.
    effect(() => {
      const conversationId = this.store.activeConversationId();

      // First run is the initial value, not a change - and there is nothing picked yet anyway.
      if (this.lastSeenConversationId === UNSET_CONVERSATION_ID) {
        this.lastSeenConversationId = conversationId;
        return;
      }
      if (this.lastSeenConversationId === conversationId) {
        return;
      }
      this.lastSeenConversationId = conversationId;

      // removeAttachment(), not a bare clear: it revokes each preview's object URL and asks the
      // service to delete an upload it has already confirmed, so an image picked and never sent
      // does not sit in the bucket until the 24h orphan sweep. Iterated over a copy, since each
      // call rebuilds the array it is walking.
      [...this.pendingAttachments].forEach((attachment) => this.removeAttachment(attachment));
    });
  }

  /* The templates bind these directly; they are the store's signals, so both surfaces render
   * the same conversation with no copying and no syncing. */
  get messages(): Signal<AiChatMessageModel[]> {
    return this.store.messages;
  }

  get isAwaitingReply(): Signal<boolean> {
    return this.store.isAwaitingReply;
  }

  get rateLimitNotice(): Signal<string | null> {
    return this.store.rateLimitNotice;
  }

  get activeConversationId(): Signal<string | null> {
    return this.store.activeConversationId;
  }

  get isLoadingConversation(): Signal<boolean> {
    return this.store.isLoadingConversation;
  }

  /** Sends the composer contents. */
  sendMessage(): void {
    const message = this.draftMessage.trim();

    // HOLD THE TURN WHILE ANY PICK IS UNFINISHED - and say so. Only the confirmed picks are sent,
    // so a turn fired over an upload that is still running, or one that failed, goes up with
    // fewer images than the strip is showing. It used to do exactly that without a word: the
    // question was answered as if no image had been attached, and the only clue was a dimmed
    // 48px thumbnail whose reason was buried in a `title` tooltip. Both cases now stop the send
    // and name what has to happen first.
    if (this.isUploadingAttachments()) {
      this.attachError = 'Still uploading. Your message will go as soon as the image is ready.';
      return;
    }

    const failed = this.failedAttachments();
    if (failed.length) {
      this.attachError = failed.length === 1
        ? `${failed[0].file.name} was not uploaded. Retry it, or remove it to send without it.`
        : `${failed.length} images were not uploaded. Retry them, or remove them to send without them.`;
      return;
    }

    const attachments = this.readyAttachments();
    if (!this.store.canSend(message, attachments.length)) {
      return;
    }

    this.draftMessage = '';
    this.resetComposerHeight();
    this.store.sendMessage(message, attachments);
    this.clearAttachments();
    // The customer just sent something - take them to it, wherever they had scrolled to.
    this.scrollToLatest(true);
  }

  /**
   * Sends a starter prompt as if the customer had typed it.
   *
   * It goes through `sendMessage()` rather than straight to the store, so a starter is subject to
   * every guard an ordinary turn is - the rate limit, an upload still running, the store's own
   * canSend(). Anything already in the composer is replaced: the starters are only on screen when
   * the transcript is empty, and a customer who has begun typing their own question and then
   * clicks an example means the example.
   */
  useStarter(prompt: string): void {
    if (this.isAwaitingReply() || this.rateLimitNotice()) {
      return;
    }

    this.draftMessage = prompt;
    this.sendMessage();
  }

  /* ############### Image attachments ############### */

  /** Opens the OS file picker. */
  openFilePicker(): void {
    this.attachError = null;
    this.fileInput?.nativeElement?.click();
  }

  /** Files chosen from the picker. The input is reset so re-picking the same file still fires. */
  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    input.value = '';
  }

  /**
   * Accepts a drop onto the composer. Files go down the same path as the picker, so the limits
   * and the messages are identical however the image arrives.
   *
   * A drop that carries no files is text, and text dropped ON THE TEXTAREA is left to the
   * browser: the native insert lands at the caret the drag positioned, respects maxlength, and
   * fires `input`, so ngModel and the auto-grow both see it - none of which a manual insert
   * reproduces. It used to be preventDefault'ed with the files, which silently ate dragged
   * text. Text dropped elsewhere on the composer is still suppressed, because the default for
   * a dragged link outside an editable field is to navigate the page to it.
   */
  onComposerDrop(event: DragEvent): void {
    this.isDragOver = false;
    const files = event.dataTransfer?.files ?? null;
    if (files?.length) {
      event.preventDefault();
      this.addFiles(files);
      return;
    }
    if (event.target !== this.composerInput?.nativeElement) {
      event.preventDefault();
    }
  }

  onComposerDragOver(event: DragEvent): void {
    // Without preventDefault the browser navigates to the dropped file instead.
    event.preventDefault();
    this.isDragOver = true;
  }

  /**
   * Pasting a copied image into the draft attaches it - the third intake beside the picker and
   * the drop, and the same addFiles path, so the limits and the per-file messages are identical
   * however an image arrives. Screenshots are the whole case: Cmd/Ctrl+V straight off the
   * clipboard used to do nothing at all.
   *
   * Only consumed when the clipboard actually carries files: a plain text paste returns before
   * preventDefault and lands in the textarea untouched. When an image travels WITH markup
   * (copying a picture from a web page puts text/html beside the file), the image is what was
   * meant - the default insert is suppressed rather than dumping HTML source into the draft.
   */
  onComposerPaste(event: ClipboardEvent): void {
    const files = event.clipboardData?.files ?? null;
    if (!files?.length) {
      return;
    }
    event.preventDefault();
    this.addFiles(files);
  }

  onComposerDragLeave(): void {
    this.isDragOver = false;
  }

  /**
   * Opens a thumbnail full-size in a dialog. One route for both kinds of tile: a pending pick
   * hands over its local object URL, a sent attachment its presigned S3 one, and the dialog
   * does not care which it was given. The url is optional because a sent attachment whose
   * presigned link failed to re-mint has none - its tile is not rendered, but the guard keeps
   * the method total.
   */
  openAttachmentPreview(url: string | null | undefined, name: string): void {
    if (!url || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const modalRef = this.modalService.open(AiChatImagePreviewModalComponent, {
      centered: true,
      windowClass: 'ai-chat-image-preview-modal',
      ariaLabelledBy: 'ai-chat-image-preview-name'
    });
    modalRef.componentInstance.imageUrl = url;
    modalRef.componentInstance.imageName = name;
  }

  /**
   * Removes one pick. An image already confirmed is deleted server-side too, so an abandoned
   * upload does not sit in the bucket waiting for the 24h orphan sweep.
   */
  removeAttachment(attachment: AiChatPendingAttachmentModel): void {
    this.pendingAttachments = this.pendingAttachments.filter((item) => item !== attachment);
    this.attachError = null;
    URL.revokeObjectURL(attachment.preview_url);

    if (attachment.attachment_uuid) {
      this.aiChatService.deleteAttachment(attachment.attachment_uuid).subscribe({
        // Best-effort: the sweep will collect it anyway, and the row is already gone on screen.
        error: () => undefined
      });
    }
  }

  /**
   * Re-runs a failed upload on the pick already on screen.
   *
   * The file is still in hand, so there is nothing to ask the customer for - re-opening the OS
   * dialog to find the same image again is a lot of work to repeat one POST. The preview's object
   * URL is deliberately kept, so the thumbnail does not blink; only the upload state is reset.
   *
   * Any `attachment_uuid` from the failed attempt is dropped rather than reused: it was authorised
   * against bytes that never landed, and confirming it would be rejected. The abandoned
   * authorisation costs nothing - the service sweeps unconfirmed uploads after 24h.
   */
  retryAttachment(attachment: AiChatPendingAttachmentModel): void {
    if (attachment.status === 'uploading') {
      return;
    }

    attachment.status = 'uploading';
    attachment.error = undefined;
    attachment.attachment_uuid = undefined;
    this.attachError = null;
    this.pendingAttachments = [...this.pendingAttachments];
    this.startUpload(attachment);
  }

  /** True while any pick is still uploading - the send button waits on this. */
  isUploadingAttachments(): boolean {
    return this.pendingAttachments.some((item) => item.status === 'uploading');
  }

  /**
   * The picks whose upload failed. They hold the turn back until retried or removed - see
   * sendMessage - and each one offers its own Retry in the strip.
   */
  failedAttachments(): AiChatPendingAttachmentModel[] {
    return this.pendingAttachments.filter((item) => item.status === 'error');
  }

  /** Whether another image may be picked. */
  canAddAttachments(): boolean {
    return this.pendingAttachments.length < AiChatConversationBase.MAX_ATTACHMENTS;
  }

  /**
   * Validates each file, then uploads it. Rejections are reported per file rather than failing
   * the whole selection, so picking four good images and one huge one still attaches the four.
   */
  private addFiles(fileList: FileList | null): void {
    if (!isPlatformBrowser(this.platformId) || !fileList?.length) {
      return;
    }

    const rejected: string[] = [];

    Array.from(fileList).forEach((file) => {
      if (!this.canAddAttachments()) {
        rejected.push(`${file.name} (limit is ${AiChatConversationBase.MAX_ATTACHMENTS} images)`);
        return;
      }
      if (!AiChatConversationBase.ACCEPTED_TYPES.includes(file.type.toLowerCase())) {
        rejected.push(`${file.name} (PNG, JPG, WEBP or GIF only)`);
        return;
      }
      if (file.size > AiChatConversationBase.MAX_ATTACHMENT_BYTES) {
        rejected.push(`${file.name} (max ${AiChatConversationBase.MAX_ATTACHMENT_MB}MB)`);
        return;
      }
      this.uploadFile(file);
    });

    this.attachError = rejected.length ? `Not added: ${rejected.join(', ')}.` : null;
  }

  /** Puts the row on screen immediately, then fills in its uuid when the upload completes. */
  private uploadFile(file: File): void {
    const pending: AiChatPendingAttachmentModel = {
      id: `${file.name}-${this.attachmentSeq++}`,
      file,
      // A local preview, so the thumbnail is there before a single byte has been sent.
      preview_url: URL.createObjectURL(file),
      status: 'uploading'
    };
    this.pendingAttachments = [...this.pendingAttachments, pending];
    this.startUpload(pending);
  }

  /**
   * Runs the three-step upload for one row and writes the outcome back onto it. Shared by the
   * first attempt and by Retry, so both report failure the same way.
   *
   * A failure is surfaced under the strip as well as on the tile: the upload happens while the
   * customer is still typing, and finding out at send time that the image never went is too late
   * to be useful.
   */
  private startUpload(pending: AiChatPendingAttachmentModel): void {
    this.aiChatService.uploadAttachment(this.conversationUuidForUpload(), pending.file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (confirmed) => {
          pending.attachment_uuid = confirmed.attachment_uuid;
          pending.status = 'ready';
          this.pendingAttachments = [...this.pendingAttachments];
        },
        error: (error: unknown) => {
          pending.status = 'error';
          pending.error = error instanceof AiChatApiError
            ? error.apiMessage
            : 'That image could not be uploaded.';
          this.pendingAttachments = [...this.pendingAttachments];
          this.attachError = `${pending.file.name}: ${pending.error}`;
        }
      });
  }

  /**
   * The conversation the images belong to. Started early if there is not one yet: the upload has
   * to name a conversation, and the customer may attach an image before typing anything.
   */
  private conversationUuidForUpload(): string {
    this.store.ensureConversation();
    return this.store.conversationId ?? '';
  }

  /** The confirmed picks, in the order they were added. */
  private readyAttachments(): AiChatAttachmentModel[] {
    return this.pendingAttachments
      .filter((item) => item.status === 'ready' && !!item.attachment_uuid)
      .map((item) => ({
        attachment_uuid: item.attachment_uuid!,
        file_name: item.file.name,
        content_type: item.file.type,
        size_bytes: item.file.size,
        url: item.preview_url
      }));
  }

  /**
   * Empties the strip after a send. The object URLs are deliberately NOT revoked here - the
   * sent message now renders those same previews, and revoking would blank them.
   */
  private clearAttachments(): void {
    this.pendingAttachments = [];
    this.attachError = null;
  }

  /**
   * Regenerates an assistant reply: drops it and asks the question again, flagged so the agent
   * gives another take rather than repeating the answer it can already see it gave.
   */
  regenerate(message: AiChatMessageModel): void {
    this.store.regenerate(message);
  }

  /**
   * Whether Regenerate applies to this message - the newest reply only, so the replacement
   * lands where the one it replaced was.
   */
  canRegenerate(message: AiChatMessageModel): boolean {
    return this.store.canRegenerate(message);
  }

  /**
   * "Worked for 27s" - how long the reply took, from the turn's `usage.elapsed_seconds`. Null
   * when the turn sent none (a stopped reply, or one reloaded from the service's history).
   */
  workedFor(message: AiChatMessageModel): string | null {
    const seconds = message.usage?.elapsed_seconds;
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
      return null;
    }
    const rounded = Math.max(1, Math.round(seconds));
    return rounded < 60
      ? `Worked for ${rounded}s`
      : `Worked for ${Math.floor(rounded / 60)}m ${rounded % 60}s`;
  }

  /**
   * Rates a reply.
   *
   * Thumbs-up, and clearing either thumb by re-clicking it, are immediate. A fresh thumbs-down
   * opens the detail form first and only counts once that is submitted - so dismissing the form
   * leaves the reply unrated rather than silently filing a bad rating with no explanation.
   */
  vote(message: AiChatMessageModel, choice: AiChatFeedbackRating): void {
    if (choice === 'down' && message.feedback !== 'down') {
      this.openFeedbackModal(message);
      return;
    }

    this.store.vote(message, choice);
  }

  /**
   * Collects the thumbs-down detail, then files it. A dismissal (Cancel, backdrop, Escape)
   * rejects the promise, which is the "changed my mind" path - leave the reply alone.
   */
  private openFeedbackModal(message: AiChatMessageModel): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const modalRef = this.modalService.open(AiChatFeedbackModalComponent, {
      centered: true,
      backdrop: 'static',
      ariaLabelledBy: 'ai-chat-feedback-title'
    });

    modalRef.result.then(
      (detail: AiChatFeedbackDetail | undefined) => {
        if (detail) {
          this.store.submitFeedback(message, detail.category, detail.note);
        }
      },
      () => {
        // Dismissed - nothing to record.
      }
    );
  }

  /**
   * Abandons the turn in flight, keeping whatever text had already arrived.
   */
  stopGenerating(): void {
    this.store.stopGenerating();
    this.focusComposer();
  }

  /**
   * Starts over with a new `conversation_uuid`. This is Clear Chat, and it is the only thing
   * that discards the transcript - a reload or a move to another page restores it instead.
   */
  startNewConversation(): void {
    this.store.startNewConversation();
    this.draftMessage = '';
    this.resetComposerHeight();
    this.focusComposer();
  }

  /**
   * Enter sends, Shift+Enter inserts a newline - the convention users expect from a chat composer.
   */
  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Copies an assistant reply to the clipboard, formatted.
   *
   * Both flavours go on at once - `text/html` and `text/plain` - and the paste target picks the
   * richest it understands. Outlook, Gmail, Teams and Word take the HTML and keep the bold, the
   * links and the table; a terminal or a code editor takes the plain text.
   *
   * This used to write the raw Markdown as plain text only, so every target got literal `**`
   * around the bold and pipe-delimited rows where the table should have been.
   */
  copyMessage(message: AiChatMessageModel): void {
    if (!isPlatformBrowser(this.platformId) || !navigator?.clipboard) {
      return;
    }

    const payload = buildAiChatClipboardPayload(message.message);
    if (!payload.text) {
      return;
    }

    this.writeToClipboard(payload)
      .then(() => this.toastService.showSuccess('Copied to clipboard'))
      .catch(() => this.toastService.showError('Could not copy. Please try again.'));
  }

  /**
   * Writes the rich pair, falling back to plain text.
   *
   * The fallback is not hypothetical: `ClipboardItem` is absent in older Safari and was long
   * unavailable in Firefox, and `clipboard.write()` can still reject where `writeText()`
   * succeeds. Either way the customer gets readable text rather than nothing.
   */
  private async writeToClipboard(payload: AiChatClipboardPayload): Promise<void> {
    const canWriteRich = typeof ClipboardItem === 'function'
      && typeof navigator.clipboard.write === 'function';

    if (canWriteRich) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([payload.html], { type: 'text/html' }),
            'text/plain': new Blob([payload.text], { type: 'text/plain' })
          })
        ]);
        return;
      } catch {
        // Fall through - a plain-text copy is far better than a failed one.
      }
    }

    await navigator.clipboard.writeText(payload.text);
  }

  /**
   * Brings this surface up on whatever the store already holds, reading storage only on the
   * first open after a load. Within a session both surfaces are already looking at the same
   * conversation, so there is nothing to restore and - crucially - nothing that could rewind a
   * turn still streaming.
   */
  protected restoreConversation(): void {
    this.store.ensureRestored();
  }

  /**
   * Tracks whether the customer is following the newest message.
   *
   * Recomputing "was I at the bottom?" from scrollTop at the moment of each update is not enough
   * on its own: a reply streams in many frames per second and each one lengthens the list, so a
   * single frame that lands while the container is momentarily mid-scroll reads as "scrolled
   * away" and the view stops following for the rest of the turn. Holding the intent as a flag,
   * changed only when the customer actually scrolls, is what makes it stick.
   */
  private isPinnedToBottom = true;
  /** Distance from the bottom, in px, still counted as being at the bottom. */
  private static readonly PIN_THRESHOLD_PX = 120;
  /** True while our own scroll is animating, so it is not mistaken for the customer scrolling. */
  private isAutoScrolling = false;
  private autoScrollTimer?: ReturnType<typeof setTimeout>;

  /**
   * Whether the window is the scroll region rather than the message list.
   *
   * Both surfaces currently scroll internally, so this is false in practice - but it is read
   * from the computed style rather than hard-coded, so a surface whose list is left to grow with
   * the document keeps working without touching this class. Auto-scroll-to-newest and the
   * pinned-to-bottom tracking both branch on it.
   */
  private usesWindowScroll(): boolean {
    const list = this.messageList?.nativeElement;
    if (!list || typeof getComputedStyle !== 'function') {
      return false;
    }
    return getComputedStyle(list).overflowY === 'visible';
  }

  /**
   * Bound to the message list's `(scroll)`, and to the window's in page mode. Scrolling up
   * unpins and the view stops following; scrolling back down re-pins it, so the customer can
   * read something earlier mid-reply and then rejoin the live answer without touching anything.
   */
  @HostListener('window:scroll')
  onMessageListScroll(): void {
    if (this.isAutoScrolling || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const list = this.messageList?.nativeElement;
    if (!list) {
      return;
    }

    if (this.usesWindowScroll()) {
      // "At the bottom" means the composer is on screen - NOT that the document is scrolled to
      // its end, which on this page is the foot of the site footer, well past the conversation.
      const composer = this.composerInput?.nativeElement;
      if (!composer) {
        return;
      }
      const distanceBelowFold = composer.getBoundingClientRect().bottom - window.innerHeight;
      this.isPinnedToBottom = distanceBelowFold < AiChatConversationBase.PIN_THRESHOLD_PX;
      return;
    }

    const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    this.isPinnedToBottom = distanceFromBottom < AiChatConversationBase.PIN_THRESHOLD_PX;
  }

  /**
   * Brings the newest message into view.
   *
   * @param force sends the view to the bottom whatever the customer was reading, and animates
   * getting there. Used for the things they just did - sending, opening the surface - where the
   * bottom is the answer to their own action. Streaming frames call this unforced, so a customer
   * who has scrolled up to re-read something is left where they are.
   *
   * Forced scrolls animate; streaming ones jump. That difference is deliberate: a smooth scroll
   * takes a few hundred ms, so starting a fresh one per frame would cancel and restart the
   * animation continuously and visibly lag behind the text. Each frame only adds a line or two,
   * so jumping per frame is what actually reads as smooth continuous motion.
   */
  protected scrollToLatest(force = false): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!force && !this.isPinnedToBottom) {
      return;
    }
    this.isPinnedToBottom = true;

    // Deferred a frame so it runs after the new content has been laid out - scrollHeight read
    // any earlier is the height without it.
    setTimeout(() => {
      const list = this.messageList?.nativeElement;
      if (!list) {
        return;
      }

      const smooth = force && !this.prefersReducedMotion();
      const behavior: ScrollBehavior = smooth ? 'smooth' : 'instant';
      this.isAutoScrolling = true;

      if (this.usesWindowScroll()) {
        // Bring the composer to the foot of the viewport, which puts the newest message just
        // above it. Scrolling the document to its end instead would land on the site footer
        // with the conversation pushed off the top.
        this.composerInput?.nativeElement?.scrollIntoView({ block: 'end', behavior });
      } else {
        list.scrollTo({ top: list.scrollHeight, behavior });
      }

      // Release the guard once the animation is over, so the next genuine scroll is read.
      clearTimeout(this.autoScrollTimer);
      this.autoScrollTimer = setTimeout(() => {
        this.isAutoScrolling = false;
      }, smooth ? 500 : 50);
    });
  }

  /**
   * Honours the OS "reduce motion" setting - an animated jump is exactly the kind of movement
   * it is meant to suppress.
   */
  private prefersReducedMotion(): boolean {
    return typeof matchMedia === 'function'
      && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Moves focus into the composer so the surface is usable from the keyboard alone.
   */
  protected focusComposer(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    setTimeout(() => this.composerInput?.nativeElement?.focus());
  }

  /**
   * Grows the composer with what is being typed, then stops and scrolls.
   *
   * A `rows="1"` textarea is a fixed one-line box that scrolls its own overflow, so the start of
   * a long question disappears upwards while it is still being written. Autosizing keeps the
   * whole draft visible up to a ceiling.
   *
   * COLLAPSE BEFORE MEASURING. `scrollHeight` never reports less than the element's current
   * height, so reading it without resetting `height` first makes the box a one-way ratchet: it
   * would grow as text is added and then stay tall after a delete, or after send cleared it.
   * `height: auto` lets the box shrink to its content so the next read is the true one.
   *
   * THE CEILING LIVES IN THE SCSS. It is read back off the element's own computed `max-height`
   * rather than duplicated here, so how far the composer may grow stays a design decision in the
   * stylesheet - and stays per-breakpoint, which a constant in this file could not be.
   * `overflow-y` is only switched on at the ceiling, so no scrollbar shows while it is growing.
   *
   * Safe on a border-box element because the composer textarea is borderless; were a border ever
   * added, `scrollHeight` would exclude it and the box would sit that much short.
   */
  onComposerInput(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const input = this.composerInput?.nativeElement;
    if (!input) {
      return;
    }

    input.style.height = 'auto';
    const content = input.scrollHeight;
    const ceiling = parseFloat(getComputedStyle(input).maxHeight);
    const height = Number.isFinite(ceiling) ? Math.min(content, ceiling) : content;

    input.style.height = `${height}px`;
    input.style.overflowY = content > height ? 'auto' : 'hidden';
  }

  /**
   * Puts the composer back to one line after its contents have been taken away.
   *
   * Deferred a tick because `draftMessage` is cleared synchronously but the textarea's `value`
   * only catches up once ngModel has written it back - measuring before that would size the box
   * to the message that was just sent.
   */
  protected resetComposerHeight(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    setTimeout(() => this.onComposerInput());
  }

  ngOnDestroy(): void {
    clearTimeout(this.autoScrollTimer);
    // Only this surface's own subscriptions (the widget's auth listener). The turn in flight is
    // the store's and deliberately outlives us - that is the whole point of the split.
    this.destroy$.next();
    this.destroy$.complete();
  }
}
